import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { wsLink } from '../../features/shared/utils/workspace-link';
import { FormulaActions } from './formula.actions';
import { FormulaSelectors } from './formula.selectors';
import { Formula } from '../../features/formula/models/formula.model';
import { FormulaService } from '../../features/formula/services/formula.service';
import { UserProfileService } from '../../features/settings/services/user-profile.service';
import { FAIL_NOTIFICATION_CONFIGURATION } from '../../features/shared/constants/notification-configurations';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import {
  WebSocketEntities,
  WebSocketReloadEvent,
} from '../../features/shared/models/web-socket/web-socket.models';
import { createWebSocketWithWarningEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import {
  createNotificationEffect,
  PushNotificationRecord,
} from '../../features/shared/utils/create-notification-effect';
import { isTransientRequestFailure } from '../../features/shared/utils/is-transient-request-failure';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { FormulaRule } from '../../features/unified-datapoint/models/formula-rule.model';
import { AssetSetting } from '../../generated/graphql-models';
import { AppActions } from '../app/app.actions';
import { ExtractionRuleListActions } from '../extraction-rule-list/extraction-rule-list.actions';
import { FormulaListActions } from '../formula-list/formula-list.actions';
import { RootState } from '../root-state.model';
import { UserProfileActions } from '../user-profile/user-profile.actions';

@Injectable()
export class FormulaEffects {
  private service = inject(FormulaService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly webSocketService = inject(WebSocketService);
  private readonly userProfileService = inject(UserProfileService);
  private readonly router = inject(Router);

  private readonly notifications: PushNotificationRecord = {
    [FormulaActions.UpdateFormulaSuccess.type]: { message: 'Formula saved', isSuccess: true },
    [FormulaActions.UpdateFormulaFail.type]: {
      message: 'Failed to save the formula',
      isSuccess: false,
    },
    [FormulaActions.SyncContextFail.type]: {
      message: 'Failed to load the context',
      isSuccess: false,
    },
    [FormulaListActions.CloneModelSuccess.type]: { message: 'Formula cloned', isSuccess: true },
    [FormulaListActions.CloneModelFail.type]: {
      message: 'Failed to clone the formula',
      isSuccess: false,
    },
    [FormulaActions.LoadFormulaFail.type]: {
      message: 'Failed to load the formula',
      isSuccess: false,
    },
  };

  redirectOnUnauthorized$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.LoadFormulaFail),
      filter((action) => !isTransientRequestFailure(action.error)),
      map(() => AppActions.Unauthorized())
    )
  );

  savePositions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.FormulaPositionChanged),
      map((action) =>
        UserProfileActions.UpdateAssetSettingRecord({
          record: {
            key: action.event.formulaId,
            path: 'recentFormulaPositions' as keyof AssetSetting,
            value: action.event.position,
          },
        })
      )
    )
  );

  loadFormula$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.LoadFormula),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        const formula = store.formula.list?.find((f) => f.id === action.id && f.unsaved);
        return (formula ? of(formula) : this.service.getById(action.id)).pipe(
          map((formula) =>
            FormulaActions.LoadFormulaSuccess({ formula, previousContext: action.previousContext })
          ),
          reportEffectError(FormulaActions.LoadFormulaFail)
        );
      })
    )
  );

  loadRecentFormulas$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.LoadRecentFormulas),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        return this.service.getRecentFormulas().pipe(
          map((formulas) => FormulaActions.LoadRecentFormulasSuccess({ formulas })),
          reportEffectError(FormulaActions.LoadRecentFormulasFail)
        );
      })
    )
  );

  removeRecentFormulas$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.RemoveRecentFormulas),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        return this.service.removeRecentFormula(action.id).pipe(
          map((formulas) => FormulaActions.RemoveRecentFormulasSuccess()),
          reportEffectError(FormulaActions.RemoveRecentFormulasFail)
        );
      })
    )
  );

  templateModalClosed$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.TemplateModalClosed),
      filter((action) => action.data && action.modalId === 'edit-formula'),
      withLatestFrom(this.store),
      map(([action, store]) =>
        FormulaActions.UpdateFormula({
          formula: {
            ...store.formula.list?.find((f) => f.id === store.formula.selectedFormulaId),
            ...action.data,
          } as Formula,
        })
      )
    )
  );

  loadFormulaSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.LoadFormulaSuccess),
      withLatestFrom(this.store),
      map(([action, store]) =>
        FormulaActions.SelectFormula({
          id: action.formula.id,
          previousVariables: action.previousContext,
        })
      )
    )
  );

  selectFormulaProxyEvent$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.SelectFormula),
      withLatestFrom(this.store),
      map(
        ([action, store]) =>
          store.formula.list?.find((i) => i.id === store.formula.selectedFormulaId)?.variables
      ),
      filter((variables) => !!variables),
      map((variables) => FormulaActions.SyncContext({ variables }))
    )
  );

  applyVersionEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.ApplyVersion),
      withLatestFrom(this.store, this.store.select(FormulaSelectors.selectedFormulaVersion)),
      map(([action, store, formula]) =>
        FormulaActions.UpdateFormula({
          formula: { ...formula, id: store.formula.selectedFormulaId },
        })
      )
    )
  );

  selectFormulaVersionEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.SelectFormulaVersion),
      withLatestFrom(this.store.select(FormulaSelectors.selectedFormula)),
      switchMap(([action, selectedFormula]) => {
        if (action.id) {
          const selectedVersion = selectedFormula.versions.find((v) => v.id === action.id);
          return this.service
            .getFormulaVersionById(selectedFormula.id, selectedVersion.version)
            .pipe(
              map((formula) =>
                FormulaActions.SelectFormulaVersionSuccess({
                  formulaVersionId: selectedVersion.id,
                  formulaVersion: formula,
                })
              )
            );
        } else {
          return of(FormulaActions.SelectFormulaVersionSuccess({}));
        }
      })
    )
  );

  selectFormulaVersionSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.SelectFormulaVersionSuccess),
      withLatestFrom(this.store),
      map(([action, store]) =>
        FormulaActions.SyncContext({ variables: action.formulaVersion?.variables })
      )
    )
  );

  updateFormulaEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.UpdateFormula),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service
          .update(action.formula, true, !!action.formula?.canShare, !!action.formula?.canPublish)
          .pipe(
            map((formula) => FormulaActions.UpdateFormulaSuccess({ formula })),
            catchError((error: HttpErrorResponse) =>
              of(FormulaActions.UpdateFormulaFail({ error }))
            )
          )
      )
    )
  );

  updateFormulaSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.UpdateFormulaSuccess),
      withLatestFrom(this.store),
      map(([action, store]) => FormulaActions.SyncContext({ variables: action.formula.variables }))
    )
  );

  syncContextEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.SyncContext),
      withLatestFrom(this.store),
      filter(([action, store]) => !!store.formula.selectedTransactionId),
      switchMap(([action, store]) => {
        const variables =
          action.variables ??
          store.formula.list?.find((f) => f.id === store.formula.selectedFormulaId)?.variables ??
          [];
        return this.service
          .getFormulaArguments(variables, store.formula.selectedTransactionId)
          .pipe(
            map((result) => FormulaActions.SyncContextSuccess({ variables: result })),
            reportEffectError(FormulaActions.SyncContextFail)
          );
      })
    )
  );

  syncContextNotificationEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.SyncContext),
      withLatestFrom(this.store),
      filter(([action, store]) => !store.formula.selectedTransactionId),
      map(() =>
        AppActions.PushNotification({
          message: 'You have to select a Transaction for the execution',
          config: FAIL_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  setTransaction$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaActions.SetTransaction),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.userProfileService
          .updateAssetSettings({
            lastSelectedTransactionForFormula: action.transactionId,
          })
          .pipe(
            map(() => FormulaActions.SetTransactionSuccess()),
            reportEffectError(FormulaActions.SetTransactionFail)
          )
      )
    )
  );

  cloneModelEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaListActions.CloneModel),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.clone(action.id).pipe(
          map((formula) => FormulaListActions.CloneModelSuccess({ formula })),
          reportEffectError(FormulaListActions.CloneModelFail)
        )
      )
    )
  );

  cloneModelSuccessNavigateEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FormulaListActions.CloneModelSuccess),
        map((action) => this.router.navigate(wsLink(SemerisRoutes.formula.root, action.formula.id)))
      ),
    { dispatch: false }
  );

  createRuleEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleListActions.CreateDatapointRuleSuccess),
      filter((action) => !!(action.rule?.rule as FormulaRule)?.formulaId),
      withLatestFrom(this.store),
      map(([action, store]) =>
        FormulaActions.LoadFormula({ id: store?.formula?.selectedFormulaId })
      )
    )
  );

  notFoundRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FormulaActions.LoadFormulaFail),
        map(() => this.router.navigate(wsLink(SemerisRoutes.formula.root)))
      ),
    { dispatch: false }
  );

  notificationEffect$: Observable<Action> = createNotificationEffect(
    this.actions$,
    this.notifications
  );

  wsEffect$: Observable<Action> = createWebSocketWithWarningEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.Formula,
    (state) => {
      window.location.reload();
    },
    WebSocketReloadEvent.FormulaReload
  );
}
